import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiShield, FiLock, FiEye, FiDatabase } from 'react-icons/fi';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-8"
        >
          <FiArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: January 2025</p>
        </div>

        {/* Privacy First Badge */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <FiShield className="w-6 h-6 text-green-600 mr-3" />
            <div>
              <h3 className="font-semibold text-green-900">Privacy First</h3>
              <p className="text-green-700 text-sm">
                AnonLink is built on zero-knowledge principles. We collect minimal data and never track users.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
            {/* Our Promise */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3 flex items-center">
                <FiLock className="w-6 h-6 mr-2 text-primary-600" />
                Our Privacy Promise
              </h2>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <ul className="space-y-2 text-gray-700">
                  <li>✓ We don't track you</li>
                  <li>✓ We don't sell your data</li>
                  <li>✓ We don't profile users</li>
                  <li>✓ We block trackers in shortened URLs</li>
                  <li>✓ We use encryption everywhere</li>
                </ul>
              </div>
            </section>

            {/* What We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3 flex items-center">
                <FiDatabase className="w-6 h-6 mr-2 text-primary-600" />
                What We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4">Account Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Email address (for login and notifications)</li>
                <li>Password (hashed with bcrypt - we never see it)</li>
                <li>Username (optional)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4">URL Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Original URLs you shorten</li>
                <li>Short URL aliases you create</li>
                <li>Total click count (no individual tracking)</li>
                <li>Creation date</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4">What We DON'T Collect</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>❌ IP addresses of link visitors</li>
                  <li>❌ Browser fingerprints</li>
                  <li>❌ Device information</li>
                  <li>❌ Location data</li>
                  <li>❌ Tracking cookies</li>
                  <li>❌ Third-party analytics</li>
                </ul>
              </div>
            </section>

            {/* How We Use Data */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">How We Use Your Data</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Authentication:</strong> To log you in securely</li>
                <li>• <strong>Service Delivery:</strong> To create and manage your shortened URLs</li>
                <li>• <strong>Communication:</strong> Account-related emails only (no marketing spam)</li>
                <li>• <strong>Security:</strong> To prevent abuse and protect the service</li>
                <li>• <strong>Billing:</strong> Pro subscriptions through Stripe (we don't store card details)</li>
              </ul>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Cookies</h2>
              <p className="text-gray-700">
                We use only essential cookies:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-700">
                <li><strong>Session cookie:</strong> Keeps you logged in</li>
                <li><strong>Guest ID:</strong> Tracks guest link limits (3 links max)</li>
              </ul>
              <p className="text-gray-700 mt-2">
                No tracking cookies. No advertising cookies. No third-party cookies.
              </p>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Sharing</h2>
              <p className="text-gray-700">
                We share your data only when:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-700">
                <li>You explicitly request it (e.g., sharing a link)</li>
                <li>Required by law (with valid legal process)</li>
                <li>Processing payments (Stripe sees billing info only)</li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-4">
                <p className="text-gray-700 font-medium">
                  We will NEVER sell, rent, or trade your data.
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Retention</h2>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Active accounts:</strong> Data kept while account is active</li>
                <li>• <strong>Deleted accounts:</strong> Removed within 30 days</li>
                <li>• <strong>Expired links:</strong> Automatically deleted after expiration</li>
                <li>• <strong>Guest sessions:</strong> Cleared after 30 days of inactivity</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3 flex items-center">
                <FiEye className="w-6 h-6 mr-2 text-primary-600" />
                Your Rights
              </h2>
              <p className="text-gray-700 mb-3">You have the right to:</p>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Access your data (via your dashboard)</li>
                <li>✓ Correct your data (in account settings)</li>
                <li>✓ Delete your data (delete account option)</li>
                <li>✓ Export your data (contact support)</li>
                <li>✓ Opt-out of emails (except security notices)</li>
              </ul>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Security Measures</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="space-y-2 text-gray-700">
                  <li>🔒 HTTPS everywhere</li>
                  <li>🔒 Passwords hashed with bcrypt</li>
                  <li>🔒 Regular security audits</li>
                  <li>🔒 Encrypted database</li>
                  <li>🔒 Rate limiting to prevent abuse</li>
                </ul>
              </div>
            </section>

            {/* Third Parties */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Third-Party Services</h2>
              <p className="text-gray-700">We use minimal third-party services:</p>
              <ul className="list-disc list-inside mt-2 text-gray-700">
                <li><strong>Stripe:</strong> Payment processing (PCI compliant)</li>
                <li><strong>Heroku:</strong> Hosting (SOC 2 compliant)</li>
                <li><strong>PostgreSQL:</strong> Database (encrypted)</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Each service is chosen for their strong privacy practices.
              </p>
            </section>

            {/* Children */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Children's Privacy</h2>
              <p className="text-gray-700">
                AnonLink is not intended for children under 13. We don't knowingly collect 
                data from children. If we learn we have, we'll delete it immediately.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Policy Changes</h2>
              <p className="text-gray-700">
                We'll notify you of significant changes via email or in-app notification. 
                Changes take effect 30 days after notice.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Contact Us</h2>
              <p className="text-gray-700">
                Privacy questions or requests? Email us at{' '}
                <a href="mailto:privacy@anonlink.com" className="text-primary-600 hover:text-primary-700">
                  privacy@anonlink.com
                </a>
              </p>
              <p className="text-gray-700 mt-2">
                We'll respond within 48 hours.
              </p>
            </section>

            {/* GDPR/CCPA */}
            <section className="border-t pt-6">
              <p className="text-sm text-gray-600">
                <strong>For EU/California residents:</strong> You have additional rights under GDPR/CCPA. 
                Contact us to exercise these rights. We don't discriminate based on privacy rights exercise.
              </p>
            </section>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <Link 
            to="/register" 
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FiShield className="w-5 h-5 mr-2" />
            Start Using AnonLink Privately
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;